/**
 * Health insurance provider lists for the insurance screen (datalist autofill).
 * Region is determined by entry.country: 'AU' → Australia list, else US list.
 */

import { INSURANCE_PROVIDERS_US } from 'hydra-data-types';
import { StoreStateInternal } from '../store/Store';
import { isReferred } from '../util/Cohorts';

// Special dropdown options that appear first in InsuranceProvider screen
export const NOT_LISTED_OPTION = 'My insurer is not listed here';

// Referred users get the larger insurance discount.
const INSURANCE_DISCOUNT = {
  direct: { couponCode: 'INRU15', displayPercent: '15%' },
  referred: { couponCode: 'INRU30', displayPercent: '30%' },
} as const;

export function getInsuranceDiscount(state: StoreStateInternal): {
  couponCode: `INRU${15 | 30}`;
  displayPercent: `${15 | 30}%`;
} {
  return isReferred(state) ? INSURANCE_DISCOUNT.referred : INSURANCE_DISCOUNT.direct;
}

// Health cover type options for the Medicare Access flow
export const HEALTH_COVER_MEDICARE_ORIGINAL = 'Original Medicare';
export const HEALTH_COVER_MEDICARE_ADVANTAGE = 'Medicare Advantage';
export const HEALTH_COVER_OTHER = 'Other';

/**
 * Stored values for the under-65 coverage plan-type question. They match hydra-data-types'
 * INSURANCE_COVERAGE_CHECK_PLAN_TYPES so web-assessment answers line up with the coverage-check
 * leads; the two Medicare values are the HEALTH_COVER_* constants above so isMedicareOriginal
 * reads both screens the same way. Labels shown to the user live in CoverageConfig.ts.
 */
export const COVERAGE_PLAN_TYPE_EMPLOYER = 'Through employer';
export const COVERAGE_PLAN_TYPE_SELF_PURCHASED = 'Self-purchased';
export const COVERAGE_PLAN_TYPE_MEDICAID = 'Medicaid';
export const COVERAGE_PLAN_TYPE_NOT_SURE = 'Not sure';

/** Exclusive answers on the coverage benefits multi-select. */
export const COVERAGE_BENEFIT_NONE = 'None of these';
export const COVERAGE_BENEFIT_NOT_SURE = 'I’m not sure';

// Medicare Advantage plan providers, ordered by membership size.
export const MEDICARE_ADVANTAGE_PROVIDERS: string[] = [
  'UnitedHealthcare',
  'Humana',
  'Aetna (CVS Health)',
  'Anthem (Elevance Health)',
  'Kaiser Permanente',
  'Centene / WellCare',
  'Health Care Service Corporation',
  'Cigna Healthcare',
  'Blue Cross Blue Shield of Michigan',
  'UPMC Health Plan',
  'SCAN Health Plan',
  'Devoted Health',
  'Florida Blue (Blue Cross Blue Shield of Florida)',
  'Highmark Blue Cross Blue Shield',
  'Independence Blue Cross',
  'Blue Shield of California',
  'Geisinger Health Plan',
  'Alignment Healthcare',
  'Horizon Blue Cross Blue Shield of New Jersey',
  'SelectHealth (Intermountain Healthcare)',
  'Healthfirst (New York)',
  'Blue Cross Blue Shield of Tennessee',
  'Medical Mutual of Ohio',
  'Priority Health (Michigan)',
  'Excellus Blue Cross Blue Shield',
  'Blue Cross Blue Shield of North Carolina',
  'Sentara Health Plans (formerly Optima Health)',
  'CareFirst Blue Cross Blue Shield',
  'Independent Health (New York)',
  'MVP Health Care',
  'Clover Health',
  'Essence Healthcare',
  "Capital District Physicians' Health Plan (CDPHP)",
  'Providence Health Plan',
  'Banner Health',
  'Network Health (Wisconsin)',
  "Martin's Point Health Care",
  'Medica',
  'Regence (Cambia Health Solutions)',
  'Capital Blue Cross',
  'EmblemHealth',
  'Quartz Health Solutions',
  'Wellmark Blue Cross Blue Shield',
  'PacificSource Health Plans',
  'Molina Healthcare',
  'Brand New Day (Universal Care)',
  'Blue Cross of Idaho',
  'Premera Blue Cross',
  'Imperial Health Plan of California',
  'Zing Health',
];

// Stored insuranceType answers that the insurance coverage selectors check
export const INSURANCE_TYPE_PUBLIC = 'Public / government program (e.g. Medicare, NHS, Medicaid)';
export const INSURANCE_TYPE_NONE = 'I do not have health cover';

/** US providers without Original Medicare — for screens only reached by non-Original users. */
export const INSURANCE_PROVIDERS_US_NON_ORIGINAL: string[] = INSURANCE_PROVIDERS_US.filter(
  (provider) => provider !== HEALTH_COVER_MEDICARE_ORIGINAL
);

export const INSURANCE_PROVIDERS_US_MEDICARE_FIRST: string[] = [
  HEALTH_COVER_MEDICARE_ORIGINAL,
  ...INSURANCE_PROVIDERS_US_NON_ORIGINAL,
];
